/**
 * Operation tools, the closed and code-defined set of program-management tools
 * (CONTEXT.md, Operation Tool). Each tool is modeled as a per-workspace
 * authorization [[Asset]]. Seeded role-to-asset grants govern access, and the
 * asset Check path enforces it (docs/adr/27-the-operations-client.md).
 *
 * This module is the one source of truth shared by the seed (server), the
 * procedure gate (server), and the `/operations` client. It owns only the
 * authz-relevant fields (key, tiers, route). The UI layer attaches its own
 * icons and labels keyed by {@link OperationToolKey}.
 *
 * It names NO role (TECH-3239). Which roles are in which tier is a tenant's own
 * vocabulary, so it belongs to the adapter whose upstream speaks it,
 * `Adapter.operationToolRoles()` in `apps/server/src/adapters/types.ts`,
 * overridden in `apps/server/src/adapters/strapi/index.ts`. Tools, keys and
 * tiers are ELASO's and stay here. The names on either side of a tier are not.
 */

/**
 * Stable identifier for an operation tool. The set is closed, so new tools are
 * added here rather than created at runtime. `route === null` tools (the
 * "orphans") are gated like the rest but have no `/operations` tab.
 */
export type OperationToolKey =
	| "dashboard"
	| "users"
	| "channels"
	| "attendance"
	| "scheduler"
	| "roles"
	| "documents"
	| "invites"
	| "broadcast" // gated, but the UI lives in /app, with no /operations tab
	| "workspace-settings" // orphan, workspace.update, no tab
	| "voice-moderation"; // orphan, livekit category kicks, no tab

/**
 * A named grouping of roles, resolved to actual role names per adapter
 * (`Adapter.operationToolRoles()`). A tool declares one for each of its two
 * grants, and nothing here says who is in one.
 *
 * `admin`, `extended` and `all` are the visibility tiers a `readTier` uses,
 * widening in that order. `teaching` is a mutate-only grouping, the admins plus
 * whoever runs a class, which is `attendance`'s manage set and is none of the
 * three.
 */
export type OpsTier = "admin" | "extended" | "all" | "teaching";

/** Every tier, for a caller that must supply or check all of them. */
export const OPS_TIERS: readonly OpsTier[] = [
	"admin",
	"extended",
	"all",
	"teaching",
];

/**
 * A tier to role-names mapping in one adapter's own vocabulary, what
 * `Adapter.operationToolRoles()` returns and what the seed grants from. Every
 * tier is present, and a tier with nobody in it is an empty array, stated
 * rather than omitted.
 */
export type OperationToolRoles = Record<OpsTier, readonly string[]>;

export interface OperationToolDef {
	key: OperationToolKey;
	/**
	 * Drives the `read_only` grant, which roles may open the tool. Mirrors the
	 * current `/operations` sidebar visibility
	 * (docs/adr/27-the-operations-client.md).
	 */
	readTier: OpsTier;
	/**
	 * Drives the `moderate` grant, which roles may mutate through the tool. This
	 * is the tier the seed grants from. The gate resolves the answer from the
	 * tool asset rather than from any role-name list at the call site
	 * (docs/adr/27-the-operations-client.md). Every procedure call goes through
	 * `authorizeProcedureCall(conn, proc, ctx)` in
	 * `apps/server/src/lib/authz/gate.ts`, and for a procedure declaring
	 * `.requireToolAccess(toolKey, "moderate")` that gate resolves this tool's
	 * asset from the connection's current workspace. The gate passes
	 * `live: true`, so the ask reaches OpenFGA rather than a warm cache
	 * (TECH-3062, TECH-3106). NOT uniformly `admin`. `documents` mutations are
	 * open to the extended set and `attendance` to the teaching grouping, so
	 * those are preserved here. Must resolve to a subset of `readTier` in every
	 * adapter's vocabulary, because anyone who can moderate can view, asserted
	 * per adapter in `apps/server/src/test/adapter-conformance.test.ts`.
	 */
	moderateTier: OpsTier;
	/** `/operations` tab route key, or `null` if the tool has no tab. */
	route: string | null;
}

/**
 * Every operation tool with its read tier, manage (moderate) tier, and
 * optional `/operations` route. Order matches the sidebar order in the UI.
 */
export const OPERATION_TOOLS: OperationToolDef[] = [
	{
		key: "dashboard",
		readTier: "all",
		moderateTier: "admin",
		route: "operationsDashboard",
	},
	{
		key: "users",
		readTier: "extended",
		moderateTier: "admin",
		route: "operationsUsers",
	},
	{
		key: "channels",
		readTier: "admin",
		moderateTier: "admin",
		route: "operationsChannels",
	},
	{
		key: "attendance",
		readTier: "all",
		moderateTier: "teaching",
		route: "operationsAttendance",
	},
	{
		key: "scheduler",
		readTier: "admin",
		moderateTier: "admin",
		route: "operationsStudioScheduler",
	},
	{
		key: "roles",
		readTier: "admin",
		moderateTier: "admin",
		route: "operationsRoles",
	},
	{
		key: "documents",
		readTier: "extended",
		moderateTier: "extended",
		route: "operationsDocuments",
	},
	{
		key: "invites",
		readTier: "admin",
		moderateTier: "admin",
		route: "operationsInvites",
	},
	{
		key: "broadcast",
		readTier: "admin",
		moderateTier: "admin",
		route: null,
	},
	{
		key: "workspace-settings",
		readTier: "admin",
		moderateTier: "admin",
		route: null,
	},
	{
		key: "voice-moderation",
		readTier: "admin",
		moderateTier: "admin",
		route: null,
	},
];

/** Look up a tool definition by key. */
export const getOperationTool = (
	key: OperationToolKey
): OperationToolDef | undefined => OPERATION_TOOLS.find((t) => t.key === key);

/**
 * The tools that have an `/operations` tab, the one definition of "there is
 * something to open here" (TECH-3284), shared by the route's entry gate and by
 * `operations.listWorkspaces`. Because both read this list, a Workspace the
 * portal offers always has at least one tab behind it, and the two answers
 * cannot drift.
 *
 * The orphans (`route === null`) are deliberately out. A grant on `broadcast`,
 * `workspace-settings` or `voice-moderation` alone would admit a user to a
 * portal whose sidebar is empty. They stay in {@link OPERATION_TOOLS} and are
 * still reported by `operations.getAccessibleTools`. `/app` reads the
 * `broadcast` one for its broadcast controls, so narrowing that payload would
 * break a control in `/app`.
 */
export const TABBED_OPERATION_TOOLS: OperationToolDef[] =
	OPERATION_TOOLS.filter((tool) => tool.route !== null);

/** Whether this tool has an `/operations` tab. */
export const isTabbedOperationTool = (key: OperationToolKey): boolean =>
	TABBED_OPERATION_TOOLS.some((tool) => tool.key === key);
